
/**
 * Hum Hena API Service
 * Adapter: Mock Data with LocalStorage Persistence
 */

import { DEFAULT_SERVICE_CATEGORIES, DEFAULT_FEE_CONFIG, MOCK_USERS, MOCK_WORKERS, MOCK_JOBS, MOCK_CMS, MOCK_REPORTS, MOCK_WITHDRAWALS, MOCK_TRANSACTIONS } from '../constants';

export const CONFIG = {
    USE_FIREBASE: false // SWITCHED TO FALSE FOR MOCK VERSION
};

// ==========================================
// MOCK DATABASE ENGINE (LocalStorage)
// ==========================================
const getMockData = (collection: string, defaults: any[]) => {
    const stored = localStorage.getItem(`hh_mock_${collection}`);
    if (stored) return JSON.parse(stored);
    // Initialize with defaults if empty
    localStorage.setItem(`hh_mock_${collection}`, JSON.stringify(defaults));
    return defaults;
};

const setMockData = (collection: string, data: any[]) => {
    localStorage.setItem(`hh_mock_${collection}`, JSON.stringify(data));
};

const updateMockItem = (collection: string, item: any, idField: string = 'id') => {
    const list = getMockData(collection, []);
    const index = list.findIndex((i: any) => i[idField] === item[idField]);
    if (index >= 0) {
        list[index] = { ...list[index], ...item };
    } else {
        list.push(item);
    }
    setMockData(collection, list);
};

const deleteMockItem = (collection: string, id: string, idField: string = 'id') => {
    const list = getMockData(collection, []);
    const filtered = list.filter((i: any) => i[idField] !== id);
    setMockData(collection, filtered);
};

// ==========================================
// IMAGE COMPRESSION HELPER
// ==========================================
const compressImage = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (event) => {
            const img = new Image();
            img.src = event.target?.result as string;
            img.onload = () => {
                const canvas = document.createElement('canvas');
                const maxWidth = 800;
                const scaleSize = maxWidth / img.width;
                const width = img.width > maxWidth ? maxWidth : img.width;
                const height = img.width > maxWidth ? img.height * scaleSize : img.height;

                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx?.drawImage(img, 0, 0, width, height);
                const compressedBase64 = canvas.toDataURL('image/jpeg', 0.7);
                resolve(compressedBase64);
            };
            img.onerror = (err) => reject(err);
        };
        reader.onerror = (err) => reject(err);
    });
};

import { supabase } from './supabaseClient'; // Import supabaseClient

// ... (keep compression helper as is or use it)

/* 
 * UPLOADS FILE TO SUPABASE STORAGE 'avatars' BUCKET
 */
export const uploadFile = async (file: File): Promise<string> => {
    try {
        const compressedBase64 = await compressImage(file);
        // Convert Base64 back to Blob for upload (or better, modify compressImage to return Blob)
        const res = await fetch(compressedBase64);
        const blob = await res.blob();

        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
        const filePath = `${fileName}`;

        const { error: uploadError } = await supabase.storage
            .from('avatars')
            .upload(filePath, blob, { contentType: 'image/jpeg' }); // Forced JPEG from compression

        if (uploadError) {
            console.error('Available Buckets Check:', await supabase.storage.listBuckets());
            throw uploadError;
        }

        // Use getPublicUrl for permanent access
        const { data: { publicUrl } } = supabase.storage.from('avatars').getPublicUrl(filePath);

        if (!publicUrl) {
            throw new Error("Failed to get public URL");
        }

        return publicUrl;
    } catch (error) {
        console.error("Upload Error:", error);
        throw error;
    }
};


// HELPER: Create Persistent Notification
const createNotification = async (userId: string, title: string, message: string, type: string) => {
    try {
        await supabase.from('notifications').insert({
            user_id: userId,
            title,
            message,
            type,
            target_group: 'INDIVIDUAL', // Default
            is_read: false,
            created_at: new Date().toISOString()
        });
    } catch (err) {
        console.error("Failed to create DB notification:", err);
    }
};

// ==========================================
// REQUEST HANDLER (MOCK ADAPTER)
// ==========================================
async function request(action: string, data: any = {}) {
    // Simulate Network Delay
    await new Promise(resolve => setTimeout(resolve, 600));

    try {
        switch (action) {
            case 'get_profile':
                // Handles Email OR Phone login lookup
                const users = getMockData('users', MOCK_USERS);
                const workers = getMockData('workers', MOCK_WORKERS);

                // Search in Users (by phone or email)
                const userFound = users.find((u: any) => u.phone === data.identifier || u.email === data.identifier);
                if (userFound) return { success: true, data: userFound };

                // Search in Workers (by phone/id or email if added)
                const workerFound = workers.find((w: any) => w.phone === data.identifier || w.id === data.identifier);
                if (workerFound) return { success: true, data: workerFound };

                return { success: true, data: null };

            case 'save_profile':
                // data.phone is used as ID for users
                updateMockItem('users', data, 'phone');
                return { success: true };

            case 'save_worker':
                const wId = data.id || data.phone;
                updateMockItem('workers', { ...data, id: wId }, 'id');
                return { success: true };

            case 'get_online_workers':
                const allWorkers = getMockData('workers', MOCK_WORKERS);
                const online = allWorkers.filter((w: any) => w.isOnline === true);
                return { success: true, data: online };

            case 'post_job':
                const newJobId = data.id || `job_${Date.now()}`;
                const jobData = {
                    ...data,
                    id: newJobId,
                    status: 'OPEN',
                    createdAt: new Date().toISOString(),
                    otp: Math.floor(1000 + Math.random() * 9000).toString(),
                    completionOtp: Math.floor(1000 + Math.random() * 9000).toString(),
                };
                updateMockItem('jobs', jobData, 'id');

                // 🔔 TRIGGER BACKEND NOTIFICATION (PHP)
                // If it's a direct booking (has workerId), send Push immediately
                if (data.workerId) {
                    createNotification(data.workerId, '📞 Direct Booking Request', `${data.customerName || 'New Customer'} wants to hire you for ${data.skill}`, 'JOB_REQUEST');
                    // Fire and forget (don't await to keep UI fast)
                    fetch('/api/send_push.php', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            worker_id: data.workerId,
                            title: '📞 Direct Booking Request',
                            body: `${data.customerName || 'New Customer'} wants to hire you for ${data.skill}`,
                            data: {
                                type: 'JOB_REQUEST',
                                jobId: newJobId,
                                customerName: data.customerName,
                                price: data.price,
                                skill: data.skill
                            }
                        })
                    }).catch(err => console.error("Push Trigger Failed:", err));
                }

                return { success: true, job_id: newJobId };

            case 'get_job':
                const allJobs = getMockData('jobs', MOCK_JOBS);
                const job = allJobs.find((j: any) => j.id === data.job_id);
                return { success: true, data: job || null };

            case 'my_jobs':
                const jobs = getMockData('jobs', MOCK_JOBS);
                let myJobs = [];
                if (data.role === 'CUSTOMER') {
                    // Match customer ID (phone/email)
                    myJobs = jobs.filter((j: any) => j.customerId === data.phone || j.customerPhone === data.phone);
                } else {
                    // Match worker ID
                    myJobs = jobs.filter((j: any) => j.workerId === data.phone);
                }
                return { success: true, data: myJobs.sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()) };

            case 'get_broadcast_jobs':
                const bJobs = getMockData('jobs', MOCK_JOBS);
                const broadcast = bJobs.filter((j: any) => j.isBroadcast && j.status === 'OPEN');
                return { success: true, data: broadcast };

            case 'update_job_status':
                const { job_id, ...updates } = data;
                const dbJobs = getMockData('jobs', MOCK_JOBS);
                const existing = dbJobs.find((j: any) => j.id === job_id);
                if (existing) {
                    const updatedJob = { ...existing, ...updates };
                    updateMockItem('jobs', updatedJob, 'id');

                    // 🔔 TRIGGER NOTIFICATIONS (Fire & Forget)
                    const notifBase = {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' }
                    };

                    // 1. Worker Accepted Job -> Notify Customer
                    if (updates.status === 'ACCEPTED' && updatedJob.customerId) {
                        createNotification(updatedJob.customerId, 'Job Accepted! ✅', `${updatedJob.workerName || 'Expert'} accepted your job.`, 'JOB_UPDATE');
                        fetch('/api/send_push.php', {
                            ...notifBase,
                            body: JSON.stringify({
                                worker_id: updatedJob.customerId, // Reusing endpoint for generic user
                                title: 'Job Accepted! ✅',
                                body: `${updatedJob.workerName || 'Expert'} accepted your job.`,
                                data: { type: 'JOB_UPDATE', jobId: job_id, status: 'ACCEPTED' }
                            })
                        }).catch(e => console.error(e));
                    }

                    // 2. Job Started -> Notify Customer
                    if (updates.status === 'IN_PROGRESS' && updatedJob.customerId) {
                        createNotification(updatedJob.customerId, 'Work Started 🛠️', `${updatedJob.workerName || 'Expert'} has started working.`, 'JOB_UPDATE');
                        fetch('/api/send_push.php', {
                            ...notifBase,
                            body: JSON.stringify({
                                worker_id: updatedJob.customerId,
                                title: 'Work Started 🛠️',
                                body: `${updatedJob.workerName || 'Expert'} has started working.`,
                                data: { type: 'JOB_UPDATE', jobId: job_id, status: 'IN_PROGRESS' }
                            })
                        }).catch(e => console.error(e));
                    }

                    // 3. Job Completed -> Notify Customer
                    if (updates.status === 'COMPLETED' && updatedJob.customerId) {
                        createNotification(updatedJob.customerId, 'Work Completed 🎉', `Job finished! Please verify and pay.`, 'JOB_UPDATE');
                        fetch('/api/send_push.php', {
                            ...notifBase,
                            body: JSON.stringify({
                                worker_id: updatedJob.customerId,
                                title: 'Work Completed 🎉',
                                body: `Job finished! Please verify and pay.`,
                                data: { type: 'JOB_UPDATE', jobId: job_id, status: 'COMPLETED' }
                            })
                        }).catch(e => console.error(e));
                    }

                    // 4. Job Cancelled -> Notify Worker
                    if (updates.status === 'CANCELLED' && updatedJob.workerId) {
                        createNotification(updatedJob.workerId, 'Job Cancelled ❌', `The customer has cancelled the job.`, 'JOB_UPDATE');
                        fetch('/api/send_push.php', {
                            ...notifBase,
                            body: JSON.stringify({
                                worker_id: updatedJob.workerId,
                                title: 'Job Cancelled ❌',
                                body: `The customer has cancelled the job.`,
                                data: { type: 'JOB_UPDATE', jobId: job_id, status: 'CANCELLED' }
                            })
                        }).catch(e => console.error(e));
                    }

                    // If job completed, create transaction record
                    if (updates.status === 'COMPLETED' && updatedJob.workerId) {
                        const txn = {
                            id: `txn_${Date.now()}`,
                            type: 'CREDIT',
                            amount: updatedJob.workerEarnings || updatedJob.price,
                            description: `Job Payment: ${updatedJob.skill}`,
                            date: new Date().toISOString(),
                            status: 'SUCCESS',
                            workerId: updatedJob.workerId
                        };
                        updateMockItem('transactions', txn, 'id');

                        // Update worker wallet
                        const allWrks = getMockData('workers', MOCK_WORKERS);
                        const wrk = allWrks.find((w: any) => w.id === updatedJob.workerId);
                        if (wrk) {
                            updateMockItem('workers', {
                                ...wrk,
                                totalEarned: (wrk.totalEarned || 0) + txn.amount,
                                lifetimeJobsCompleted: (wrk.lifetimeJobsCompleted || 0) + 1
                            }, 'id');
                        }
                    }
                }
                return { success: true };

            case 'get_categories':
                const cats = getMockData('categories', DEFAULT_SERVICE_CATEGORIES);
                return { success: true, data: cats };

            case 'save_category':
                updateMockItem('categories', data, 'id');
                return { success: true };

            case 'get_platform_config':
                const conf = getMockData('config', [DEFAULT_FEE_CONFIG]);
                return { success: true, data: conf[0] || DEFAULT_FEE_CONFIG };

            case 'save_platform_config':
                setMockData('config', [data]);
                return { success: true };

            case 'get_ads':
                return { success: true, data: getMockData('ads', []) };

            case 'save_ad':
                updateMockItem('ads', data, 'id');
                return { success: true };

            case 'delete_ad':
                deleteMockItem('ads', data.id, 'id');
                return { success: true };

            case 'admin_get_users':
                return { success: true, data: getMockData('users', MOCK_USERS) };

            case 'admin_get_workers':
                return { success: true, data: getMockData('workers', MOCK_WORKERS) };

            case 'admin_delete_user':
                deleteMockItem('users', data.phone, 'phone');
                return { success: true };

            case 'admin_delete_worker':
                deleteMockItem('workers', data.worker_id, 'id');
                return { success: true };

            case 'verify_admin':
                if (data.email === 'admin' && data.password === 'admin') {
                    return { success: true, data: { firstName: 'Admin', role: 'ADMIN' } };
                }
                return { success: false, error: 'Invalid Mock Credentials (try admin/admin)' };

            // --- Missing Handlers Implemented Below ---

            case 'admin_get_reports':
                return { success: true, data: getMockData('reports', MOCK_REPORTS) };

            case 'admin_update_report_status':
                updateMockItem('reports', { id: data.id, status: data.status }, 'id');
                return { success: true };

            case 'request_withdrawal':
                const wdRequest = {
                    ...data,
                    id: `wd_${Date.now()}`,
                    status: 'PENDING',
                    requestedAt: new Date().toISOString()
                };
                updateMockItem('withdrawals', wdRequest, 'id');
                return { success: true };

            case 'admin_get_withdrawals':
                return { success: true, data: getMockData('withdrawals', MOCK_WITHDRAWALS) };

            case 'admin_update_withdrawal_status':
                updateMockItem('withdrawals', { id: data.id, status: data.status }, 'id');
                return { success: true };

            case 'get_worker_transactions':
                const allTxns = getMockData('transactions', MOCK_TRANSACTIONS);
                const workerTxns = allTxns.filter((t: any) => t.workerId === data.workerId);
                return { success: true, data: workerTxns };

            case 'get_cms_content':
                return { success: true, data: getMockData('cms', MOCK_CMS) };

            case 'save_cms_content':
                updateMockItem('cms', data, 'slug');
                return { success: true };

            case 'get_api_keys':
                return { success: true, data: getMockData('api_keys', [{ googleMaps: '' }])[0] };

            case 'save_api_keys':
                setMockData('api_keys', [data]);
                return { success: true };

            case 'get_firebase_config':
                return { success: true, data: getMockData('firebase_config', [{}])[0] };

            case 'save_firebase_config':
                setMockData('firebase_config', [data]);
                return { success: true };

            default:
                console.warn("API action not found:", action);
                return { success: true, data: [] };
        }
    } catch (e: any) {
        console.error("Mock API Error:", e);
        return { success: false, error: e.message };
    }
}

export const api = {
    // Note: getProfile now sends identifier to match Phone or Email
    getProfile: (identifier: string) => request('get_profile', { identifier }),
    saveProfile: (profile: any) => request('save_profile', profile),
    getOnlineWorkers: () => request('get_online_workers'),
    saveWorker: (workerData: any) => request('save_worker', workerData),
    toggleOnline: (phone: string, isOnline: boolean, coords: any) => request('save_worker', { phone, isOnline, ...coords }),
    postJob: (jobData: any) => request('post_job', jobData),
    getJob: (jobId: string) => request('get_job', { job_id: jobId }),
    getBroadcastJobs: () => request('get_broadcast_jobs'),
    updateJobStatus: (jobId: string, status: string, extra?: any) => request('update_job_status', { job_id: jobId, status, ...extra }),
    getMyJobs: (phone: string, role: string) => request('my_jobs', { phone, role }),
    getWorkerTransactions: (workerId: string) => request('get_worker_transactions', { workerId }),
    requestWithdrawal: (data: any) => request('request_withdrawal', data),
    getStats: () => request('admin_get_stats'),
    getAllUsers: () => request('admin_get_users'),
    getAllWorkers: () => request('admin_get_workers'),
    deleteUser: (phone: string) => request('admin_delete_user', { phone }),
    deleteWorker: (workerId: string) => request('admin_delete_worker', { worker_id: workerId }),
    updateUserStatus: (phone: string, status: string) => request('save_profile', { phone, status }),
    updateWorkerStatus: (workerId: string, status: string) => request('save_worker', { id: workerId, status }),
    getCategories: () => request('get_categories'),
    saveCategory: (cat: any) => request('save_category', cat),
    getPlatformConfig: () => request('get_platform_config'),
    savePlatformConfig: (config: any) => request('save_platform_config', config),
    getReports: () => request('admin_get_reports'),
    updateReportStatus: (id: string, status: string) => request('admin_update_report_status', { id, status }),
    getWithdrawals: () => request('admin_get_withdrawals'),
    updateWithdrawalStatus: (id: string, status: string) => request('admin_update_withdrawal_status', { id, status }),
    getCMSContent: () => request('get_cms_content'),
    saveCMSContent: (data: any) => request('save_cms_content', data),
    resetUserPassword: (phone: string) => Promise.resolve({ success: true }),
    getAds: () => request('get_ads'),
    saveAd: (ad: any) => request('save_ad', ad),
    deleteAd: (id: string) => request('delete_ad', { id }),
    getFirebaseConfig: () => request('get_firebase_config'),
    saveFirebaseConfig: (config: any) => request('save_firebase_config', config),
    getApiKeys: () => request('get_api_keys'),
    saveApiKeys: (keys: any) => request('save_api_keys', keys),
    verifyAdminCredentials: (email: string, password: string) => request('verify_admin', { email, password }),
};
